import { zodResolver } from "@hookform/resolvers/zod";
import {
	getEquipmentQueryOptions,
	getInspectionTemplateQueryOptions,
} from "@packages/api-client/hooks/equipmentController";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSubmitInspection } from "@/api/equipment";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormRootMessage,
	useGuardedSubmit,
} from "@/components/ui/form";
import { FormActionBar } from "@/components/ui/form-action-bar";
import { NumberField } from "@/components/ui/number-field";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { SubmitButton } from "@/components/ui/submit-button";
import { Textarea } from "@/components/ui/textarea";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { cn } from "@/utils/utils";
import {
	type InspectionFormValues,
	inspectionSchema,
	toInspectionPayload,
} from "./-components/inspectionSchema";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/equipment/fleet/$equipmentId/inspect",
)({
	// Inspections need the server-side template and don't queue yet — a future
	// slice may make them queueable field events; until then, online-only.
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	loader: async ({ context, params }) => {
		await Promise.all([
			context.queryClient.ensureQueryData(
				getEquipmentQueryOptions(params.orgId, params.equipmentId),
			),
			context.queryClient.ensureQueryData(
				getInspectionTemplateQueryOptions(params.orgId, params.equipmentId),
			),
		]);
	},
	component: InspectionPage,
});

const DEFECT_SEVERITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"] as const;

function InspectionPage() {
	const { orgId, equipmentId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();

	const { data: equipment } = useSuspenseQuery(
		getEquipmentQueryOptions(orgId, equipmentId),
	);

	const { data: template } = useSuspenseQuery(
		getInspectionTemplateQueryOptions(orgId, equipmentId),
	);

	const submitMutation = useSubmitInspection();

	const form = useForm<InspectionFormValues>({
		resolver: zodResolver(inspectionSchema),
		mode: "onTouched",
		reValidateMode: "onChange",
		defaultValues: {
			hourMeterReading: equipment?.currentHours?.toString() ?? "",
			items: (template?.sections ?? []).flatMap((section) =>
				(section.items ?? []).map((item: { id: string; label: string }) => ({
					templateItemId: item.id,
					label: item.label,
					section: section.name,
					result: "PASS" as const,
					defectSeverity: undefined,
					defectNotes: undefined,
				})),
			),
		},
	});

	const items = form.watch("items");

	const setResult = (index: number, result: "PASS" | "FAIL") => {
		form.setValue(`items.${index}.result`, result, { shouldValidate: true });
		if (result === "FAIL" && !items[index]?.defectSeverity) {
			form.setValue(`items.${index}.defectSeverity`, "MEDIUM");
		}
	};

	const failedItems = items.filter((i) => i.result === "FAIL");
	const criticalItems = failedItems.filter(
		(i) => i.defectSeverity === "CRITICAL",
	);
	const hasCritical = criticalItems.length > 0;

	const onValid = (values: InspectionFormValues) =>
		submitMutation
			.mutateAsync({
				organizationId: orgId,
				equipmentId,
				data: toInspectionPayload(values),
			})
			.then(() => {
				navigate({
					to: "/app/org/$orgId/equipment/fleet/$equipmentId",
					params: { orgId, equipmentId },
				});
			});

	const guardedSubmit = useGuardedSubmit(form, onValid);

	// Group item indices by section for rendering (index === field-array index).
	const sections = items.reduce<Record<string, number[]>>(
		(acc, item, index) => {
			const bucket = acc[item.section] ?? [];
			bucket.push(index);
			acc[item.section] = bucket;
			return acc;
		},
		{},
	);

	return (
		<div className="flex flex-col gap-4 max-w-lg mx-auto w-full">
			<PageHeader title={t("equipment.inspection.title")} showBackButton />

			<Form {...form}>
				<form onSubmit={guardedSubmit} className="flex flex-col gap-4">
					{/* Equipment Header */}
					<Card>
						<CardContent className="py-3">
							<div className="flex items-center justify-between">
								<div>
									<p className="font-medium">{equipment?.name}</p>
									<p className="text-xs text-base-content/60">
										{equipment?.assetId}
									</p>
								</div>
								<Badge variant="outline">
									{t(
										`equipment.categories.${equipment?.category}`,
										equipment?.category ?? "",
									)}
								</Badge>
							</div>
						</CardContent>
					</Card>

					{/* Hour Meter */}
					<Card>
						<CardContent className="py-4">
							<FormField
								control={form.control}
								name="hourMeterReading"
								render={({ field }) => (
									<FormItem>
										<FormLabel required className="text-base font-medium">
											{t("equipment.inspection.hourMeter")}
										</FormLabel>
										<FormControl>
											<NumberField
												placeholder="0"
												suffix="h"
												className="text-lg h-12"
												value={field.value}
												onChange={field.onChange}
												onBlur={field.onBlur}
												name={field.name}
												ref={field.ref}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</CardContent>
					</Card>

					{/* Checklist Sections */}
					{Object.entries(sections).map(([sectionName, indices]) => (
						<Card key={sectionName}>
							<CardHeader className="pb-2">
								<CardTitle className="text-sm">{sectionName}</CardTitle>
							</CardHeader>
							<CardContent className="space-y-3">
								{indices.map((index) => {
									const item = items[index];
									return (
										<div key={item.templateItemId} className="space-y-2">
											<div className="flex items-center justify-between gap-2">
												<span className="text-sm">{item.label}</span>
												<div className="flex gap-1">
													<button
														type="button"
														onClick={() => setResult(index, "PASS")}
														className={cn(
															"p-1.5 rounded-lg border transition-colors",
															item.result === "PASS"
																? "bg-success/10 border-success text-success"
																: "border-base-300 text-base-content/40 hover:border-success/50",
														)}
													>
														<CheckCircle2 className="h-5 w-5" />
													</button>
													<button
														type="button"
														onClick={() => setResult(index, "FAIL")}
														className={cn(
															"p-1.5 rounded-lg border transition-colors",
															item.result === "FAIL"
																? "bg-error/10 border-error text-error"
																: "border-base-300 text-base-content/40 hover:border-error/50",
														)}
													>
														<XCircle className="h-5 w-5" />
													</button>
												</div>
											</div>

											{item.result === "FAIL" && (
												<div className="ml-2 pl-3 border-l-2 border-error/30 space-y-2">
													<FormField
														control={form.control}
														name={`items.${index}.defectSeverity`}
														render={({ field }) => (
															<FormItem>
																<Select
																	value={field.value ?? "MEDIUM"}
																	onValueChange={field.onChange}
																>
																	<FormControl>
																		<SelectTrigger className="h-8 text-sm">
																			<SelectValue />
																		</SelectTrigger>
																	</FormControl>
																	<SelectContent>
																		{DEFECT_SEVERITIES.map((sev) => (
																			<SelectItem key={sev} value={sev}>
																				{t(
																					`equipment.inspection.severity.${sev}`,
																				)}
																			</SelectItem>
																		))}
																	</SelectContent>
																</Select>
																<FormMessage />
															</FormItem>
														)}
													/>
													<FormField
														control={form.control}
														name={`items.${index}.defectNotes`}
														render={({ field }) => (
															<FormItem>
																<FormControl>
																	<Textarea
																		placeholder={t(
																			"equipment.inspection.defectNotesPlaceholder",
																		)}
																		rows={2}
																		className="text-sm"
																		value={field.value ?? ""}
																		onChange={field.onChange}
																		onBlur={field.onBlur}
																		name={field.name}
																		ref={field.ref}
																	/>
																</FormControl>
															</FormItem>
														)}
													/>
												</div>
											)}
										</div>
									);
								})}
							</CardContent>
						</Card>
					))}

					{/* Defect Summary */}
					{failedItems.length > 0 && (
						<Card
							className={cn(
								"border-2",
								hasCritical ? "border-error" : "border-warning",
							)}
						>
							<CardContent className="py-3">
								<div className="flex items-center gap-2 mb-2">
									<AlertTriangle
										className={cn(
											"h-4 w-4",
											hasCritical ? "text-error" : "text-warning",
										)}
									/>
									<span className="font-medium text-sm">
										{failedItems.length}{" "}
										{t("equipment.inspection.defectsFound")}
									</span>
								</div>
								{hasCritical && (
									<div className="p-2 rounded bg-error/10 text-error text-sm font-medium">
										{t("equipment.inspection.unsafeToOperate")}
									</div>
								)}
							</CardContent>
						</Card>
					)}

					<FormRootMessage />

					{/* Submit Bar */}
					<FormActionBar className="-mx-3 px-3 md:-mx-4 md:px-4">
						<SubmitButton pending={submitMutation.isPending} size="lg">
							{t("equipment.inspection.submit")}
						</SubmitButton>
					</FormActionBar>
				</form>
			</Form>
		</div>
	);
}
